import { ReactElement, ReactNode } from 'react'
import { salesReportCopy } from '../copy'
import { formatCents, formatPercent, formatShare, Gender, genderLabel, TicketType } from '../format'
import { HorizontalScroll } from '../HorizontalScroll'
import { LoteBadge } from '../LoteBadge'
import { ProgressBar } from '../ProgressBar'
import { StockHint } from '../StockHint'
import { TipoBadge } from '../TipoBadge'
import {
  gridLine,
  gridTemplate,
  printGridTemplate,
  SalesTableColumnId,
  SalesTableColumns,
  tableMinWidth,
  visibleColumns,
} from './columns'
import * as S from './styles'

export type { SalesTableColumns } from './columns'

const HEADER_LABELS: Record<SalesTableColumnId, string> = {
  ticket: 'Ingresso',
  lot: 'Lote',
  gender: 'Sexo',
  type: 'Tipo',
  quantity: 'Qtd',
  stock: salesReportCopy.stockColumn,
  unitValue: 'Valor Unit.',
  fee: 'Taxa',
  paymentMethod: 'Forma de pagamento',
  total: 'Total',
}

const formatCount = (value: number): string => value.toLocaleString('pt-BR')

interface SalesTableScrollProps {
  columns: SalesTableColumns
  ariaLabel: string
  inParentScroll?: boolean
  children: ReactNode
}

export const salesTableMinWidth = (columns: SalesTableColumns): number => tableMinWidth(columns) + 8

export function SalesTableScroll({ columns, ariaLabel, inParentScroll = false, children }: SalesTableScrollProps): ReactElement {
  const table = (
    <S.Content
      $minWidth={tableMinWidth(columns)}
      role="table"
      aria-label={ariaLabel}
      aria-colcount={visibleColumns(columns).length}
    >
      {children}
    </S.Content>
  )
  return <S.Scroll>{inParentScroll ? table : <HorizontalScroll label={`${ariaLabel}, com rolagem lateral`}>{table}</HorizontalScroll>}</S.Scroll>
}

export function SalesTableHeader({ columns }: { columns: SalesTableColumns }): ReactElement {
  return (
    <S.HeaderBox $template={gridTemplate(columns)} $printTemplate={printGridTemplate(columns)} role="row">
      {visibleColumns(columns).map((column, index) => (
        <S.Cell key={column.id} $align={column.align} $sticky={index === 0 ? 'header' : undefined} role="columnheader">
          <S.HeaderText>{HEADER_LABELS[column.id]}</S.HeaderText>
        </S.Cell>
      ))}
    </S.HeaderBox>
  )
}

export interface SalesTableGroupHeaderData {
  label: string
  quantity: number
  stockText?: string
  issuedPercentage?: number | null
}

interface SalesTableGroupProps {
  header?: SalesTableGroupHeaderData
  children: ReactNode
}

export function SalesTableGroup({ header, children }: SalesTableGroupProps): ReactElement {
  return (
    <S.GroupCard role="rowgroup">
      {header && (
        <S.GroupHeader role="row">
          <S.GroupHeaderContent role="cell">
            <S.GroupName>{header.label}</S.GroupName>
            <S.Pill>{salesReportCopy.ticketsPill(formatCount(header.quantity))}</S.Pill>
            {header.stockText && <S.GroupStock>{header.stockText}</S.GroupStock>}
            {header.issuedPercentage != null && (
              <S.ProgressWrap>
                <ProgressBar
                  percentage={header.issuedPercentage}
                  label={salesReportCopy.issuedShare(formatPercent(header.issuedPercentage))}
                />
                <S.ProgressLabel>{salesReportCopy.issuedShare(formatPercent(header.issuedPercentage))}</S.ProgressLabel>
              </S.ProgressWrap>
            )}
          </S.GroupHeaderContent>
        </S.GroupHeader>
      )}
      {children}
    </S.GroupCard>
  )
}

export interface SalesTableRowData {
  key: string
  ticket: string
  lot: string
  lotPercentage: number | null
  gender: Gender
  ticketType: TicketType
  courtesy: boolean
  quantity: number
  stock?: { text: string, low?: boolean, hint?: string }
  unitValueCents: number
  feeCents?: number
  paymentMethod?: string
  totalCents: number
}

interface SalesTableRowProps {
  row: SalesTableRowData
  columns: SalesTableColumns
  index: number
}

export function SalesTableRow({ row, columns, index }: SalesTableRowProps): ReactElement {
  return (
    <S.Row $template={gridTemplate(columns)} $printTemplate={printGridTemplate(columns)} $striped={index % 2 === 1} role="row">
      <S.Cell role="cell" $sticky={index % 2 === 1 ? 'zebra' : 'row'}>
        <S.TicketText title={row.ticket}>{row.ticket}</S.TicketText>
      </S.Cell>
      <S.Cell role="cell">
        <S.LotText>{row.lot}</S.LotText>
        <LoteBadge percentage={row.lotPercentage} />
      </S.Cell>
      <S.Cell role="cell">
        <S.MutedText>{genderLabel(row.gender)}</S.MutedText>
      </S.Cell>
      <S.Cell role="cell" $align="center" $padding="10px 8px">
        <TipoBadge ticketType={row.ticketType} courtesy={row.courtesy} />
      </S.Cell>
      <S.Cell role="cell" $align="center">
        <S.QuantityText>{formatCount(row.quantity)}</S.QuantityText>
      </S.Cell>
      {columns.showStock && (
        <S.Cell role="cell" $align="center" $overflow="visible">
          <StockHint text={row.stock?.text ?? '—'} hint={row.stock?.hint} low={row.stock?.low === true} />
        </S.Cell>
      )}
      <S.Cell role="cell">
        <S.ValueText>{formatCents(row.unitValueCents)}</S.ValueText>
      </S.Cell>
      {columns.showFee && (
        <S.Cell role="cell">
          <S.ValueText>{row.feeCents == null ? '—' : formatCents(row.feeCents)}</S.ValueText>
        </S.Cell>
      )}
      {columns.showPaymentMethod && (
        <S.Cell role="cell">
          <S.PaymentText>{row.paymentMethod ?? '—'}</S.PaymentText>
        </S.Cell>
      )}
      <S.Cell role="cell">
        <S.TotalText>{formatCents(row.totalCents)}</S.TotalText>
      </S.Cell>
    </S.Row>
  )
}

export interface SalesTableSubtotalData {
  label: string
  quantity: number
  revenueShare: number | null
  stockText?: string
  totalCents: number
  feeCents?: number
}

interface SalesTableSubtotalProps {
  subtotal: SalesTableSubtotalData
  columns: SalesTableColumns
}

export function SalesTableSubtotal({ subtotal, columns }: SalesTableSubtotalProps): ReactElement {
  return (
    <S.SubtotalRow $template={gridTemplate(columns)} $printTemplate={printGridTemplate(columns)} role="row">
      <S.Cell role="cell" aria-colindex={1} $sticky="subtotal">
        <S.SubtotalLabel>{subtotal.label}</S.SubtotalLabel>
      </S.Cell>
      <S.Cell
        role="cell"
        aria-colindex={gridLine(columns, 'lot')}
        aria-colspan={3}
        style={{ gridColumn: `${gridLine(columns, 'lot')} / span 3` }}
      >
        {subtotal.revenueShare != null && (
          <S.ProgressWrap $width="100%">
            <ProgressBar
              percentage={subtotal.revenueShare}
              label={salesReportCopy.revenueShare(formatShare(subtotal.revenueShare))}
            />
            <S.ProgressLabel>{salesReportCopy.revenueShare(formatShare(subtotal.revenueShare))}</S.ProgressLabel>
          </S.ProgressWrap>
        )}
      </S.Cell>
      <S.Cell role="cell" aria-colindex={gridLine(columns, 'quantity')} $align="center">
        <S.StrongText>{formatCount(subtotal.quantity)}</S.StrongText>
      </S.Cell>
      {columns.showStock && (
        <S.Cell role="cell" aria-colindex={gridLine(columns, 'stock')} $align="center">
          <S.StrongText>{subtotal.stockText ?? ''}</S.StrongText>
        </S.Cell>
      )}
      <S.Cell role="cell" aria-colindex={gridLine(columns, 'unitValue')} />
      {columns.showFee && (
        <S.Cell role="cell" aria-colindex={gridLine(columns, 'fee')}>
          <S.StrongText>{subtotal.feeCents == null ? '' : formatCents(subtotal.feeCents)}</S.StrongText>
        </S.Cell>
      )}
      {columns.showPaymentMethod && <S.Cell role="cell" aria-colindex={gridLine(columns, 'paymentMethod')} />}
      <S.Cell role="cell" aria-colindex={gridLine(columns, 'total')}>
        <S.SubtotalTotal>{formatCents(subtotal.totalCents)}</S.SubtotalTotal>
      </S.Cell>
    </S.SubtotalRow>
  )
}

export interface SalesTableTotalData {
  label: string
  quantity: number
  totalCents: number
  feeCents?: number
  stockText?: string
}

interface SalesTableTotalProps {
  total: SalesTableTotalData
  columns: SalesTableColumns
}

export function SalesTableTotal({ total, columns }: SalesTableTotalProps): ReactElement {
  const quantityLine = gridLine(columns, 'quantity')
  return (
    <S.TotalBar $template={gridTemplate(columns)} $printTemplate={printGridTemplate(columns)} role="row">
      <S.Cell
        role="cell"
        aria-colindex={1}
        aria-colspan={quantityLine - 1}
        style={{ gridColumn: `1 / ${quantityLine}` }}
      >
        <S.TotalLabel>{total.label}</S.TotalLabel>
      </S.Cell>
      <S.Cell role="cell" aria-colindex={quantityLine} $align="center" style={{ gridColumn: `${quantityLine} / span 1` }}>
        <S.TotalQuantity>{formatCount(total.quantity)}</S.TotalQuantity>
      </S.Cell>
      {columns.showStock && total.stockText && (
        <S.Cell
          role="cell"
          aria-colindex={gridLine(columns, 'stock')}
          $align="center"
          style={{ gridColumn: `${gridLine(columns, 'stock')} / span 1` }}
        >
          <S.TotalQuantity>{total.stockText}</S.TotalQuantity>
        </S.Cell>
      )}
      {columns.showFee && total.feeCents != null && (
        <S.Cell
          role="cell"
          aria-colindex={gridLine(columns, 'fee')}
          style={{ gridColumn: `${gridLine(columns, 'fee')} / span 1` }}
        >
          <S.TotalQuantity>{formatCents(total.feeCents)}</S.TotalQuantity>
        </S.Cell>
      )}
      <S.Cell role="cell" aria-colindex={gridLine(columns, 'total')} $align="right" style={{ gridColumn: '-2 / -1' }}>
        <S.TotalValue>{formatCents(total.totalCents)}</S.TotalValue>
      </S.Cell>
    </S.TotalBar>
  )
}
